<template>
  <el-collapse-transition>
    <fieldset v-show="opened">
      <legend ng-if="label || $slots.label">
        <slot name="label">{{label}}</slot>
      </legend>
      <p v-if="$slots.default">
        <slot></slot>
      </p>
      <el-form-sub
        :prop="prop"
        :model="{variables:variables}"
        :rules="rules"
        @valid="v => formValid = v">
        <input-headers-group>
          <input-header>Variable name</input-header>
          <input-header>
            Value
            <template slot="tooltip">
              A comma separated list of values, the first non <code>null</code> value is chosen.<br/>
              Example: <code>@variable, @condition ? $other : null, "value"</code>
            </template>
          </input-header>
          <input-header width="56px"></input-header>
        </input-headers-group>
        <comparator :view="variables" :compare="otherVariables">
          <template v-slot="{ value, index, compare }">
            <el-form-errors>
              <el-button-group style="width: 100%">
                <comparable prop="name" default=""
                  :ignore="value.temporary"
                  :required="!value.temporary"
                  :rules="[VariableValidator].concat(value.temporary ? [] : duplicateRule(value.key))">
                  <template v-slot="{ view, compare, attrs, status }">
                    <el-form-small-item :class="'comparable-' + status"
                      :prop="view && 'variables.'+index+'.name'"
                      v-bind="attrs"
                      field="name">
                      <el-entity-autocomplete
                        v-model="value.name"
                        placeholder="variable name"
                        :type="type"
                        :suggestions="filterVariableSuggestions(value.key)"
                        :tabindex="opened ? 0 : -1"
                        :disabled="compare"></el-entity-autocomplete>
                    </el-form-small-item>
                  </template>
                </comparable>
                <comparable v-if="hasNLU && value.name === 'NLU'"
                  prop="value" default="" diff
                  :ignore="value.temporary"
                  @input="v => value.value = v ? 'true' : ''">
                  <template v-slot="{ view, compare, listeners, status }">
                    <el-checkbox border class="variables-editor-nlu"
                      :class="'comparable-' + status"
                      :value="value.value === 'true'"
                      v-on="listeners"
                      :disabled="compare">
                      NLU
                    </el-checkbox>
                  </template>
                </comparable>
                <comparable v-else
                  prop="value" default=""
                  :ignore="value.temporary"
                  :required="!value.temporary && !value.fixed">
                  <template v-slot="{ view, compare, attrs }">
                    <el-form-item-code-input
                      v-model="value.value"
                      :prop="view && 'variables.'+index+'.value'"
                      v-bind="attrs"
                      field="value"
                      :varprefix="varprefix"
                      :varstatus="varstatus"
                      :disabled="compare || value.temporary"
                      placeholder="variable value"
                      :tabindex="opened ? 0 : -1"></el-form-item-code-input>
                  </template>
                </comparable>
                <el-button
                  :icon="value.temporary ? 'el-icon-circle-plus-outline' : 'el-icon-delete'"
                  :disabled="compare || value.temporary || value.fixed"
                  @click="deleteVariable(value.key)"
                  :tabindex="-1"></el-button>
              </el-button-group>
            </el-form-errors>
          </template>
        </comparator>
      </el-form-sub>
      <p v-if="$slots.after">
        <slot name="after"></slot>
      </p>
    </fieldset>
  </el-collapse-transition>
</template>

<script>
import { VariableValidator } from '../../utils/validators.js'

import ElEntityAutocomplete from '../element/ElEntityAutocomplete.vue'
import ElFormItemCodeInput from '../element/ElFormItemCodeInput.vue'

export default {
  name: 'VariablesEditor',
  components: {
    ElEntityAutocomplete,
    ElFormItemCodeInput,
  },
  props: {
    value: Object,
    other: Object,
    prop: String,
    active: {
      type: Boolean,
      default: true,
    },
    forced: Boolean,
    closed: Boolean,
    type: {
      type: String,
      default: 'variable',
    },
    localVariables: Array,
    contextVariables: Array,
    variableSuggestions: Array,
    variableNames: Array,
    variableNew: {
      type: Boolean,
      default: true,
    },
    rules: Object,
    label: {
      type: String,
      default: 'Variables',
    },
    // the possible variable prefix
    varprefix: Object,
    // the status of each type of variable
    varstatus: Object,
  },
  data () {
    return {
      variables: [],
      VariableValidator: VariableValidator,
      formValid: true,
    }
  },
  computed: {
    otherVariables () {
      if (!this.other) return this.other
      let variables = [], seen = {}
      for (let name of this.variableNames || []) {
        variables.push({
          name: name,
          value: this.other[name] || '',
          key: Math.random() + '',
          fixed: true,
        })
        seen[name] = true
      }
      for (let name in this.other) {
        if (name in seen) continue
        variables.push({
          key: Math.random() + '',
          name: name,
          value: this.other[name],
        })
      }
      return variables
    },
    hasNLU () {
      return !!(this.variableNames && this.variableNames
        .find(x => x === 'NLU'))
    },
    computedForced () {
      if (this.forced || !this.formValid) return true
      for (let name in this.value || {}) return true
      for (let name in this.other || {}) return true
      if (this.variableNames && this.variableNames.length) return true
      return false
    },
    status () {
      if (!this.formValid) return 'error'
      else if (this.computedForced) return 'forced'
      else if (this.closed) return 'closed'
      else if (this.active) return 'active'
      else return 'inactive'
    },
    // if should open the slider
    opened () {
      return this.active && !closed || this.computedForced
    },
  },
  watch: {
    value () { this.updateVariables() },
    variables: {
      handler () {
        this.variablesUpdated()
      },
      deep: true,
    },
    variableNames (value) {
      value = value || []
      let variables = [], names = {}, items = {}, extra = []
      for (let name of value) names[name] = true
      for (let variable of this.variables) {
        if (!variable.temporary && names[variable.name]) {
          items[variable.name] = variable
          delete names[variable.name]
          variable.fixed = true
        } else if (this.variableNew && (!variable.fixed || variable.value)) {
          extra.push(variable)
          delete variable.fixed
        }
      }
      for (let name of value) {
        variables.push(items[name] || {
          name: name,
          value: '',
          key: Math.random() + '',
          fixed: true,
        })
      }
      this.variables = variables.concat(extra)
    },
    variableNew (value) {
      if (value) {
        if (!this.variables.find(variable => variable.temporary)) {
          this.variables.push({
            name: '',
            value: '',
            key: Math.random() + '',
            temporary: true,
          })
        }
      } else {
        this.variables = this.variables.filter(variable => variable.fixed)
      }
    },
    status () { this.handleStatus() },
  },
  created () {
    this.updateVariables()
    this.handleStatus()
  },
  methods: {
    handleStatus () {
      this.$emit('status', this.status)
      this.$emit('button', {
        'status': this.status,
        'icon': this.status === 'active' ? 'el-icon-caret-top'
          : this.status === 'inactive' ? 'el-icon-caret-bottom'
            : this.status === 'error' ? 'el-icon-error' : 'el-icon-minus',
        'disabled': this.status !== 'active' && this.status !== 'inactive',
        'next': this.status !== 'active',
      })
    },
    updateVariables () {
      let value = this.value || {}
      let variables = [], keys = {}, seen = {}
      for (let variable of this.variables) {
        if (variable.temporary) continue
        else if (variable.name in keys) keys[variable.name] = null
        else keys[variable.name] = variable.key
      }
      for (let name of this.variableNames || []) {
        variables.push({
          name: name,
          value: value[name] || '',
          key: keys[name] || Math.random() + '',
          fixed: true,
        })
        seen[name] = true
      }
      let names = []
      for (let name in value) {
        if (!seen[name]) names.push(name)
      }
      names.sort()
      for (let name of names) {
        variables.push({
          name: name,
          value: value[name],
          key: keys[name] || Math.random() + '',
        })
      }
      let vcopy = variables.slice()
      vcopy.sort((a, b) => (a.name > b.name) - (a.name < b.name))
      let copy = this.variables.filter(v => !v.temporary)
      copy.sort((a, b) => (a.name > b.name) - (a.name < b.name))
      let temporary = this.variables.find(v => v.temporary)
      if (this.variableNew) {
        variables.push(temporary || {
          name: '',
          value: '',
          key: Math.random() + '',
          temporary: true,
        })
      }
      if (!temporary !== !this.variableNew || vcopy.length !== copy.length) {
        this.variables = variables
        return
      }
      for (let i = 0; i < copy.length; i++) {
        let a = vcopy[i], b = copy[i]
        if (a.name !== b.name || a.value !== b.value || a.fixed !== b.fixed) {
          this.variables = variables
          return
        }
      }
    },
    variablesUpdated () {
      let temporary = this.variables.find(v => v.temporary)
      if (temporary && temporary.name) {
        delete temporary.temporary
        temporary = null
      }
      if (!temporary && this.variableNew) {
        this.variables.push({
          name: '',
          value: '',
          key: Math.random() + '',
          temporary: true,
        })
      }
      let value = {}, seen = {}
      for (let variable of this.variables) {
        if (variable.temporary) continue
        else if (!variable.name || variable.name in seen) return
        else if (!variable.fixed || variable.value) {
          value[variable.name] = variable.value
        }
        seen[variable.name] = true
      }
      let same = !!this.value
      if (same) {
        for (let name in value) {
          if (!(name in this.value) || this.value[name] !== value[name]) {
            same = false
            break
          }
        }
      }
      if (same) {
        for (let name in this.value) {
          if (!(name in this)) {
            same = false
            break
          }
        }
      }
      if (!same) {
        this.$emit('input', value)
        this.$emit('change', value)
      }
    },
    deleteVariable (key) {
      for (let i = 0; i < this.variables.length; i++) {
        if (this.variables[i].key === key && !this.variables[i].temporary) {
          this.variables.splice(i, 1)
          return
        }
      }
      throw new TypeError('key "' + key + '" not found')
    },
    filterVariableSuggestions (key) {
      let seen = {}
      for (let v of this.variables) {
        if (v.key !== key) seen[v.name] = true
      }
      return (this.variableSuggestions || []).filter(v => !seen[v.value])
    },
    duplicateRule (key) {
      return {
        validator: (rule, value, callback) => {
          callback(this.checkDuplicateName(value, key))
        },
      }
    },
    checkDuplicateName (name, key) {
      for (let v of this.variables) {
        if (v.name === name && v.key !== key) {
          return new Error('Variable defined multiple times')
        }
      }
    },
  },
}
</script>

<style lang="scss">
  @import "../../element-variables.scss";

  .variables-editor-nlu {
    flex: $field-flex !important;
  }
</style>
